import React, { useState, useEffect } from 'react';
import {
  View,
  StyleSheet,
  Text,
  ScrollView,
  Alert,
  FlatList,
  Platform,
  Dimensions,
  Keyboard,
  type StyleProp,
  type ViewStyle,
} from 'react-native';
import { Button, IconButton, Searchbar, List, Divider, Modal, Portal, TextInput } from 'react-native-paper';
import NetInfo from '@react-native-community/netinfo';
import { Room } from '../types';
import { reviewService } from '../services/reviewService';
import { theme, colors } from '../constants/theme';

interface RoomDropdownProps {
  propertyId: number;
  selectedRoomId?: number;
  onRoomSelect: (roomId: number | undefined) => void;
  style?: StyleProp<ViewStyle>;
  label?: string;
}

const isConflictResponseError = (error: unknown): boolean => {
  if (typeof error !== 'object' || error === null || !('response' in error)) {
    return false;
  }

  const response = (error as { response?: { status?: unknown } }).response;
  return response?.status === 409;
};

export const RoomDropdown: React.FC<RoomDropdownProps> = ({
  propertyId,
  selectedRoomId,
  onRoomSelect,
  style,
  label = "Location",
}) => {
  const [rooms, setRooms] = useState<Room[]>([]);
  const [filteredRooms, setFilteredRooms] = useState<Room[]>([]);
  const [selectedRoom, setSelectedRoom] = useState<Room | null>(null);
  const [showSelectionModal, setShowSelectionModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [pendingAddModal, setPendingAddModal] = useState(false);
  const [newRoomName, setNewRoomName] = useState('');
  const [addingRoom, setAddingRoom] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadRooms();
  }, [propertyId]);

  useEffect(() => {
    if (selectedRoomId && rooms.length > 0) {
      const room = rooms.find(r => r.id === selectedRoomId);
      if (room) {
        setSelectedRoom(room);
      }
    }
  }, [selectedRoomId, rooms]);

  // Monitor network state and reload rooms when coming back online
  useEffect(() => {
    const unsubscribe = NetInfo.addEventListener(state => {
      if (state.isConnected && state.isInternetReachable) {
        // Reload rooms when network comes back
        loadRooms();
      }
    });

    return () => unsubscribe();
  }, [propertyId]);

  // Refresh rooms when dropdown opens to catch any synced rooms
  useEffect(() => {
    if (showSelectionModal) {
      loadRooms();
    }
  }, [showSelectionModal, propertyId]);

  useEffect(() => {
    // Filter rooms based on search query
    if (searchQuery) {
      const filtered = rooms.filter(room =>
        room.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredRooms(filtered);
    } else {
      setFilteredRooms(rooms);
    }
  }, [searchQuery, rooms]);

  // Handle opening add modal after selection modal closes
  useEffect(() => {
    if (!showSelectionModal && pendingAddModal) {
      const timer = setTimeout(() => {
        setPendingAddModal(false);
        setShowAddModal(true);
      }, Platform.OS === 'ios' ? 350 : 0);

      return () => clearTimeout(timer);
    }
  }, [showSelectionModal, pendingAddModal]);

  const loadRooms = async () => {
    try {
      setLoading(true);
      const fetchedRooms = await reviewService.getRooms(propertyId);
      setRooms(fetchedRooms);
    } catch (error) {
      console.error('Failed to load rooms:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddRoom = async () => {
    if (!newRoomName.trim()) {
      Alert.alert('Error', 'Please enter a location name');
      return;
    }

    if (!propertyId) {
      Alert.alert('Error', 'Cannot add location - property information missing');
      return;
    }

    try {
      setAddingRoom(true);
      const newRoom = await reviewService.createRoom(propertyId, newRoomName.trim());
      
      // Add to local list and select it
      setRooms([...rooms, newRoom]);
      setSelectedRoom(newRoom);
      onRoomSelect(newRoom.id);
      
      // Close modal and reset
      setShowAddModal(false);
      setNewRoomName('');
      Keyboard.dismiss();
      
      Alert.alert('Success', 'Location added successfully');
    } catch (error: unknown) {
      if (isConflictResponseError(error)) {
        Alert.alert('Error', 'A location with this name already exists');
      } else {
        Alert.alert('Error', 'Failed to add location');
      }
    } finally {
      setAddingRoom(false);
    }
  };

  const handleRoomSelect = (room: Room) => {
    setSelectedRoom(room);
    onRoomSelect(room.id);
    setShowSelectionModal(false);
    setSearchQuery('');
  };

  const handleClearSelection = () => {
    setSelectedRoom(null);
    onRoomSelect(undefined);
    setShowSelectionModal(false);
    setSearchQuery('');
  };

  const openSelectionModal = () => {
    setShowSelectionModal(true);
    setSearchQuery('');
  };

  const openAddRoomModal = () => {
    setPendingAddModal(true);
    setShowSelectionModal(false);
  };

  return (
    <View style={[styles.container, style]}>
      <Text style={styles.label}>{label}</Text>
      <Button
        testID="room-dropdown-button"
        mode="outlined"
        onPress={openSelectionModal}
        loading={loading}
        style={styles.dropdown}
        contentStyle={styles.dropdownContent}
        icon="map-marker"
      >
        {selectedRoom ? selectedRoom.name : 'Select a location'}
      </Button>

      {/* Location Selection Modal */}
      <Portal>
        <Modal
          testID="room-selection-modal"
          visible={showSelectionModal}
          onDismiss={() => {
            setShowSelectionModal(false);
            setSearchQuery('');
          }}
          contentContainerStyle={styles.selectionModalContent}
        >
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Select Location</Text>
              <IconButton
                icon="close"
                size={24}
                onPress={() => {
                  setShowSelectionModal(false);
                  setSearchQuery('');
                }}
              />
            </View>
            
            <Searchbar
              testID="room-search-input"
              placeholder="Search locations..."
              onChangeText={setSearchQuery}
              value={searchQuery}
              style={styles.searchBar}
              autoFocus={Platform.OS !== 'ios'}
            />

            <Button
              testID="room-add-new-button"
              mode="text"
              icon="plus"
              onPress={openAddRoomModal}
              style={styles.addNewButton}
              contentStyle={styles.addNewButtonContent}
              labelStyle={styles.addNewTitle}
            >
              Add New Location...
            </Button>
            
            <FlatList
              testID="room-list"
              data={filteredRooms}
              keyExtractor={(item) => item.id.toString()}
              removeClippedSubviews={Platform.OS === 'ios'}
              initialNumToRender={10}
              maxToRenderPerBatch={10}
              renderItem={({ item }) => (
                <List.Item
                  testID={`room-option-${item.id}`}
                  title={item.name}
                  onPress={() => handleRoomSelect(item)}
                  style={[
                    styles.listItem,
                    selectedRoom?.id === item.id && styles.selectedListItem
                  ]}
                  left={(props) => <List.Icon {...props} icon="map-marker" />}
                  right={(props) => selectedRoom?.id === item.id ? 
                    <List.Icon {...props} icon="check" color={theme.colors.primary} /> : null}
                />
              )}
              ListEmptyComponent={
                <View style={styles.emptyContainer}>
                  <Text style={styles.emptyText}>No locations found</Text>
                </View>
              }
              ItemSeparatorComponent={() => <Divider />}
              ListHeaderComponent={selectedRoom ? (
                <>
                  <List.Item
                    testID="room-clear-selection-button"
                    title="Clear Selection"
                    onPress={handleClearSelection}
                    style={styles.clearSelectionItem}
                    titleStyle={styles.clearSelectionTitle}
                    left={(props) => <List.Icon {...props} icon="close-circle-outline" color={theme.colors.error} />}
                  />
                  <Divider />
                </>
              ) : null}
              style={styles.list}
            />
        </Modal>
      </Portal>

      <Portal>
        <Modal
          testID="room-add-modal"
          visible={showAddModal}
          onDismiss={() => {
            setShowAddModal(false);
            setNewRoomName('');
          }}
          contentContainerStyle={[
            styles.addModalContent,
            Platform.OS !== 'web' && { maxHeight: Dimensions.get('window').height * 0.6 }
          ]}
        >
          <ScrollView
            contentContainerStyle={{ flexGrow: 1 }}
            keyboardShouldPersistTaps="handled"
          >
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Add New Location</Text>
              <Button
                testID="room-add-button-top"
                mode="text"
                onPress={handleAddRoom}
                loading={addingRoom}
                disabled={!newRoomName.trim() || addingRoom}
                compact
              >
                Add
              </Button>
              <IconButton
                icon="close"
                size={24}
                onPress={() => setShowAddModal(false)}
              />
            </View>

            <TextInput
              testID="room-name-input"
              style={styles.input}
              placeholder="Enter location name"
              value={newRoomName}
              onChangeText={setNewRoomName}
              autoFocus={Platform.OS === 'web'}
              onSubmitEditing={handleAddRoom}
            />

            <View style={styles.modalButtons}>
              <Button
                testID="room-add-cancel-button"
                mode="text"
                onPress={() => {
                  setShowAddModal(false);
                  setNewRoomName('');
                }}
                disabled={addingRoom}
              >
                Cancel
              </Button>
              <Button
                testID="room-add-button"
                mode="contained"
                onPress={handleAddRoom}
                loading={addingRoom}
                disabled={!newRoomName.trim() || addingRoom}
              >
                Add Location
              </Button>
            </View>
          </ScrollView>
        </Modal>
      </Portal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginVertical: 10,
  },
  label: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 8,
    color: '#333',
  },
  dropdown: {
    borderColor: colors.border,
  },
  dropdownContent: {
    flexDirection: 'row-reverse',
    justifyContent: 'space-between',
  },
  selectionModalContent: {
    backgroundColor: 'white',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    height: Platform.OS === 'web' ? '80%' : '80%',
    maxHeight: Platform.OS === 'web' ? 600 : undefined,
    paddingTop: 10,
    margin: Platform.OS === 'web' ? 20 : 0,
    marginTop: Platform.OS === 'web' ? 'auto' : 0,
    // Drawer-like feel on mobile, centered on web/tablet
    alignSelf: Platform.OS === 'web' ? 'center' : 'stretch',
    width: Platform.OS === 'web' ? '90%' : '100%',
    maxWidth: Platform.OS === 'web' ? 600 : undefined,
  },
  searchBar: {
    marginHorizontal: 16,
    marginBottom: 10,
    backgroundColor: '#f5f5f5',
  },
  list: {
    flex: 1,
  },
  listItem: {
    paddingHorizontal: 16,
  },
  selectedListItem: {
    backgroundColor: colors.gray100,
  },
  emptyContainer: {
    padding: 20,
    alignItems: 'center',
  },
  emptyText: {
    color: '#666',
    fontSize: 16,
  },
  addNewButton: {
    alignSelf: 'flex-start',
    marginHorizontal: 16,
    marginBottom: 8,
    backgroundColor: '#f9f9f9',
  },
  addNewButtonContent: {
    justifyContent: 'flex-start',
  },
  addNewTitle: {
    color: theme.colors.primary,
    fontWeight: '600',
  },
  clearSelectionItem: {
    backgroundColor: '#fff5f5',
  },
  clearSelectionTitle: {
    color: theme.colors.error,
    fontWeight: '600',
  },
  addModalContent: {
    backgroundColor: 'white',
    borderRadius: 10,
    padding: 20,
    margin: 20,
    width: Platform.OS === 'web' ? '90%' : undefined,
    maxWidth: 400,
    alignSelf: 'center',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  modalTitle: {
    flex: 1,
    fontSize: 20,
    fontWeight: '600',
  },
  input: {
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 5,
    padding: 12,
    fontSize: 16,
    marginBottom: 20,
  },
  modalButtons: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 10,
  },
});
